import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserByJWT } from '../../store/slices/authSlice';
import { getMessages, fetchUnreadMessage } from '../../utils/api';
import ChatPanel from './ChatPanel';

const Messages = ({ isWidget = false, onCloseWidget }) => {
    const dispatch = useDispatch();
    
    
    // Redux state (only for auth)
    const user = useSelector((state) => state.auth.user);
    const jwtToken = useSelector((state) => state.auth.jwtToken);
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    const socketConnection = useSelector((state) => state.common?.socket);

    // Widget state: minimized bar vs expanded panel
    const [isMinimized, setIsMinimized] = useState(true);

    const userAvatar = user?.image || user?.avatar || null;
    const userInitial = (user?.display_name || user?.user_email || 'M').charAt(0).toUpperCase();
    
    // Local component state
    const [conversations, setConversations] = useState([]);
    const [conversationsPagination, setConversationsPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
    });
    
    const [activeConversation, setActiveConversation] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [localSearch, setLocalSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isNewMessageMode, setIsNewMessageMode] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Fetch unread message count (used on load and when socket receives new message)
    const fetchUnreadCount = async () => {
        if (!jwtToken) return;
        try {
            const response = await fetchUnreadMessage();
            const count = response?.data?.count ?? response?.data ?? 0;
            setUnreadCount(!isNaN(Number(count)) ? Number(count) : 0);
        } catch (err) {
            setUnreadCount(0);
        }
    };

    // Helper function to get conversation ID from URL
    const getConversationIdFromUrl = () => {
        if (isWidget) {
            return null;
        }
        const path = window.location.pathname;
        // Match pattern: /messages/view/123 or /messages/view/123/
        const params = new URLSearchParams(window.location.search);
        const viewParam = params.get('view');
        return viewParam ? parseInt(viewParam) : null;
    };
    
    // Helper function to update URL
    const updateUrlWithConversation = (conversationId) => {
        if (isWidget) {
            return;
        }
        // if (conversationId) {
        //     const basePath = window.location.pathname.split('/messages')[0];
        //     const newUrl = `${basePath}/messages/?view=${conversationId}`;
        //     window.history.pushState({ conversationId }, '', newUrl);
        // }
    };
    

    // Fetch user info on mount FIRST (priority)
    useEffect(() => {
        if (isWidget) {
            return;
        }
        if (jwtToken && !user) {
            console.log('Fetching user with JWT token...');
            dispatch(fetchUserByJWT(jwtToken));
        }
    }, [jwtToken, user, dispatch]);
    
    // Log when user is loaded
    useEffect(() => {
        if (user) {
            console.log('User loaded:', user);
        }
    }, [user]);

    useEffect(() => {
  
        if(socketConnection){
    
          socketConnection.on('response', (data) => {
            console.log('Received from server:', data);
          });
          socketConnection.on('sendMessageRes', (data) => {
            setConversationsPagination(prev => ({ ...prev, page: 1 }));
            fetchConversationsData(1, true); // Skip loading state for socket updates
            fetchUnreadCount();
          });
    
        }
    
      }, [socketConnection]);
    
    
    // Check URL for conversation ID on mount and when URL changes
    useEffect(() => {
        const conversationIdFromUrl = getConversationIdFromUrl();
        if (conversationIdFromUrl && conversationIdFromUrl !== activeConversation) {
            console.log('Setting active conversation from URL:', conversationIdFromUrl);
            setActiveConversation(conversationIdFromUrl);
        }
        
        // Handle browser back/forward navigation
        const handlePopState = (event) => {
            const conversationId = event.state?.conversationId || getConversationIdFromUrl();
            if (conversationId) {
                setActiveConversation(conversationId);
            } else {
                // If no conversation in URL, clear active conversation
                setActiveConversation(null);
            }
        };
        
        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isWidget]);
    
    // Fetch conversations from external API (matches React Native fetchMessages logic)
    const fetchConversationsData = async (pageNumber = 1, skipLoading = false) => {
        if (!jwtToken) return;
        
        if (!skipLoading) {
            setIsLoading(true);
        }
        setError(null);
        
        
        try {
            const response = await getMessages({
                search: searchQuery,
                limit: conversationsPagination.limit,
                page: pageNumber,
            });
            
            console.log('API Response:', response);
            
            // Handle response (matches React Native logic)
            if (response && response.data && response.data.messages && response.data.messages.length > 0) {
                const fetchedConversations = response.data.messages;
                
                // Update conversations - merge or replace based on page (like React Native)
                setConversations(prevConversations => {
                    if (pageNumber === 1) {
                        return fetchedConversations;
                    }
                    
                    // Merge and remove duplicates using Map
                    const allConversations = [...prevConversations, ...fetchedConversations];
                    const uniqueConversations = Array.from(
                        new Map(allConversations.map(conv => [conv.id, conv])).values()
                    );
                    
                    // Sort by date_sent (newest first)
                    return uniqueConversations.sort((a, b) =>
                        new Date(b.date_sent || b.updated_at || 0) - new Date(a.date_sent || a.updated_at || 0)
                    );
                });
                
                // Check if there are more conversations to load
                const hasMoreConversations = fetchedConversations.length === conversationsPagination.limit;
                setConversationsPagination(prev => ({ 
                    ...prev, 
                    page: pageNumber,
                    has_next: hasMoreConversations,
                    has_prev: pageNumber > 1
                }));
                
                // Do not auto-select any conversation by default.
                // User will explicitly choose a thread to open.
            } else {
                // No more conversations to load
                setConversationsPagination(prev => ({ ...prev, has_next: false }));
                
                // If no data and first page, set empty
                if (pageNumber === 1) {
                    setConversations([]);
                }
            }
            
            // Update pagination from response if available
            if (response.pagination) {
                setConversationsPagination(prev => ({
                    ...prev,
                    ...response.pagination
                }));
            }
        } catch (err) {
            console.error('Failed to fetch conversations:', err);
            setError(err.message || 'Failed to load conversations');
        } finally {
            if (!skipLoading) {
                setIsLoading(false);
            }
        }
    };
    
    // Fetch conversations and unread count ONLY AFTER user is loaded
    useEffect(() => {
        if (jwtToken && user) {
            console.log('User is loaded, now fetching conversations...');
            setConversationsPagination(prev => ({ ...prev, page: 1 }));
            setConversations([]);
            fetchConversationsData(1);
            fetchUnreadCount();
        }
    }, [jwtToken, user]); // Depends on both jwtToken AND user
    
    // Debounced search - wait 1100ms after typing stops (matches React Native)
    useEffect(() => {
        if (!user) return; // Don't search if user not loaded yet
        
        if (searchQuery.length > 0) {
            const debounceTimer = setTimeout(() => {
                setConversationsPagination(prev => ({ ...prev, page: 1 }));
                setConversations([]);
                fetchConversationsData(1);
            }, 1100);
            
            return () => clearTimeout(debounceTimer);
        } else if (searchQuery.length === 0 && conversations.length === 0 && user) {
            // When search cleared, fetch immediately (only if user is loaded)
            setConversationsPagination(prev => ({ ...prev, page: 1 }));
            fetchConversationsData(1);
        }
    }, [searchQuery, user]); // Depends on user being loaded

    // Event handlers
    const handleConversationClick = (conversationId) => {
        setIsNewMessageMode(false);
        setActiveConversation(conversationId);
        // Update URL
        updateUrlWithConversation(conversationId);
        // Mark as read - update unread_count in recipients array
        setConversations(prev => 
            prev.map(c => {
                // Match by thread_id (which is what we pass from onClick)
                if (c.thread_id === conversationId || c.id === conversationId) {
                    // Update recipients to set unread_count to 0 for current user
                    const updatedRecipients = c.recipients?.map(recipient => {
                        if (recipient.user_id === user?.id) {
                            return { ...recipient, unread_count: 0 };
                        }
                        return recipient;
                    }) || c.recipients;
                    
                    return { 
                        ...c, 
                        unread: false,
                        recipients: updatedRecipients
                    };
                }
                return c;
            })
        );
        fetchUnreadCount();
    };

    const handleSearchChange = (e) => {
        const text = e.target.value;
        setLocalSearch(text);
        setSearchQuery(text);
        
        // When cleared, reset and fetch immediately (like React Native)
        if (text.length === 0) {
            setConversationsPagination(prev => ({ ...prev, page: 1 }));
            setConversations([]);
            fetchConversationsData(1);
        }
    };
    
    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            setSearchQuery(localSearch);
            setConversationsPagination(prev => ({ ...prev, page: 1 }));
            setConversations([]);
            fetchConversationsData(1);
        }
    };
    
    // Load more conversations (infinite scroll - like React Native handleEndReached)
    const handleLoadMoreConversations = () => {
        if (!isLoading && conversationsPagination.has_next) {
            const nextPage = conversationsPagination.page + 1;
            setConversationsPagination(prev => ({ ...prev, page: nextPage }));
            fetchConversationsData(nextPage);
        }
    };
    
    // Refresh conversations (like React Native onRefresh)
    const handleRefreshConversations = () => {
        setConversationsPagination(prev => ({ ...prev, page: 1 }));
        setConversations([]);
        fetchConversationsData(1);
    };
    
    // Conversations pagination handlers
    const handleNextPage = () => {
        if (conversationsPagination.has_next && !isLoading) {
            handleLoadMoreConversations();
        }
    };
    
    const handlePrevPage = () => {
        if (conversationsPagination.has_prev) {
            const prevPage = conversationsPagination.page - 1;
            setConversationsPagination(prev => ({ ...prev, page: prevPage }));
            setConversations([]);
            fetchConversationsData(prevPage);
        }
    };

    // Format date for conversation list (like design)
    const formatDate = (date) => {
        if (!date) return '';
        
        const messageDate = new Date(date);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        // Today: show time (e.g., "10:34 AM")
        if (messageDate.toDateString() === today.toDateString()) {
            return messageDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } 
        // Yesterday: show "Yesterday"
        else if (messageDate.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } 
        // Older: show abbreviated date (e.g., "Oct 9")
        else {
            const now = new Date();
            const showYear = messageDate.getFullYear() !== now.getFullYear();
            
            return messageDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                ...(showYear && { year: 'numeric' })
            });
        }
    };
    
    // Strip HTML tags (like React Native)
    const stripHtmlTags = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '');
    };
    
    // Decode HTML entities
    const decodeHtml = (html) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    };
    
    // Computed values
    const activeConv = conversations.find(c => c.thread_id === activeConversation);
    
    // Check if mobile view should show chat
    const shouldShowChat = activeConversation || isNewMessageMode;
    
    // Handle back button click for mobile
    const handleBackToConversations = () => {
        setActiveConversation(null);
        setIsNewMessageMode(false);
        // Clear URL
        const basePath = window.location.pathname.split('/messages')[0];
        const newUrl = `${basePath}/messages/`;
        //window.history.pushState({}, '', newUrl);
    };

    // Show loading if user is not loaded yet
    // if (user && jwtToken) {
    //     return (
    //         <div className="octopus-messages-container">
    //             <div className="octopus-loading-user">
    //                 <div className="octopus-spinner"></div>
    //                 <p>Loading user data...</p>
    //             </div>
    //         </div>
    //     );
    // }
    
    // Show login message if no token
    if (!jwtToken) {
        return (
            <div className="octopus-messages-container">
                <div className="octopus-not-logged-in">
                    <p>Please log in to view messages.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`octopus-messages-container ${(isMinimized && !activeConversation && !isNewMessageMode) ? 'octopus-messages-height-minimized' : ''} ${isMinimized ? 'octopus-messages-container-minimized' : ''} ${shouldShowChat ? 'show-chat' : ''} ${isWidget ? 'octopus-messages-widget' : ''}`} >
            {/* Left Panel - Chat Window (hidden until a conversation or new message is active) */}
            {(activeConversation || isNewMessageMode) && (
                <ChatPanel 
                    activeConversation={activeConversation}
                    activeConv={activeConv}
                    isNewMessageMode={isNewMessageMode}
                    onBack={handleBackToConversations}
                    onNewMessageCancel={() => {
                        setIsNewMessageMode(false);
                        setActiveConversation(null);
                        handleBackToConversations();
                    }}
                    onNewMessageSent={(threadId) => {
                        setIsNewMessageMode(false);
                        setActiveConversation(threadId);
                        updateUrlWithConversation(threadId);
                        // Refresh conversations list
                        handleRefreshConversations();
                    }}
                />
            )}
            {/* Right Panel - Chat Window */}
            <div className={`octopus-messages-sidebar ${(isMinimized && !activeConversation && !isNewMessageMode)  ? 'octopus-messages-sidebar-minimized' : ''}`}>
                {!isMinimized ? (
                    <div className="octopus-messages-header-container">
                        <div className="octopus-messages-header">
                            <div className="octopus-chat-widget-bar-avatar online-user">
                                {userAvatar ? (
                                    <img src={userAvatar} alt="" />
                                ) : (
                                    <span className="octopus-chat-widget-bar-initial">{userInitial}</span>
                                )}
                            </div>
                            <span className="octopus-chat-widget-bar-label">Messaging</span>
                            <div className="octopus-messages-header-actions">
                                <span 
                                    className="octopus-icon bb-icon-f header-new-message bb-icon-edit"
                                    onClick={() => {
                                        setIsNewMessageMode(true);
                                        setActiveConversation(null);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    title="New message"
                                ></span>
                                {/* Minimize widget button (uses same SVG as ChatWidget button) */}
                                {isWidget && (
                                    <button
                                        type="button"
                                        className="octopus-chat-widget-panel-btn octopus-messages-widget-close"
                                        onClick={() => { setIsMinimized(true); fetchUnreadCount(); }}     
                                        aria-label="Minimize"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                                        </svg>
                                    </button>
                                )}
                                

                                
                            </div>
                        </div>
                        
                        <div className="octopus-messages-search">
                            <span className="octopus-search-icon">
                                <i className="bb-icon-f bb-icon-search"></i>
                            </span>
                            <input
                                type="text"
                                placeholder="Search messages..."
                                value={localSearch}
                                onChange={handleSearchChange}
                                onKeyPress={handleSearchSubmit}
                            />
                            <button 
                                className="octopus-search-button" 
                                onClick={handleSearchSubmit}
                            >
                                Search
                            </button>
                        </div>

                        <div className="octopus-conversations-list">
                            {isLoading && conversations.length === 0 ? (
                                <div className="octopus-loading-conversations">Loading conversations...</div>
                            ) : conversations.length === 0 ? (
                                <div className="octopus-no-conversations">No conversations found</div>
                            ) : (
                                <>
                                    {conversations.map(conversation => {
                                        // Get unread count for current user (like React Native)
                                        const unreadMessages = conversation.recipients?.filter(
                                            recipient => recipient.user_id === user?.id && recipient.unread_count > 0
                                        ) || [];
                                        
                                        // Determine conversation name (group or recipients)
                                        let conversationName = '';
                                        if (conversation.group && conversation.group.id) {
                                            conversationName = conversation.group.name;
                                        } else if (conversation.recipients && conversation.recipients.length > 0) {
                                            conversationName = conversation.recipients
                                                .filter(recipient => user?.id !== recipient.userData?.id)
                                                .map(recipient => recipient.userData?.display_name)
                                                .filter(name => name)
                                                .join(', ') || 'Deleted user';
                                        } else {
                                            conversationName = conversation.name || conversation.title || conversation.sender || 'Conversation';
                                        }

                                        // Get avatar
                                        const firstOtherUserRecipient = conversation.recipients?.find(
                                            recipient => recipient.userData?.id !== user?.id
                                        );
                                        const avatar = conversation.group?.image ||
                                            firstOtherUserRecipient?.userData?.image ||
                                            conversation.avatar || '💬';
                                        
                                        // Get last message preview
                                        const lastMessageText = conversation.message ? 
                                            decodeHtml(stripHtmlTags(conversation.message)) : 
                                            (conversation.lastMessage || conversation.last_message || '');
                                        
                                        const messageSender = conversation.userData?.id === user?.id ? 'You' : 
                                                            (conversation.userData?.display_name || '');
                                        
                                        return (
                                            <div
                                                key={conversation.id}
                                                className={`octopus-conversation-item ${conversation.thread_id === activeConversation ? 'active' : ''}`}
                                                onClick={() => handleConversationClick(conversation.thread_id)}
                                            >
                                                <div className="octopus-conversation-avatar">
                                                    {typeof avatar === 'string' && avatar.startsWith('http') ? (
                                                        <img src={avatar} alt={conversationName} className="octopus-avatar-img" />
                                                    ) : (
                                                        <span>{avatar}</span>
                                                    )}
                                                </div>
                                                <div className="octopus-conversation-content">
                                                    <div className="octopus-conversation-header">
                                                        <span className="octopus-conversation-name">
                                                            {conversationName}
                                                        </span>
                                                        {unreadMessages.length > 0 && (
                                                            <span className="octopus-unread-dot"></span>
                                                        )}
                                                    </div>
                                                    <div className="octopus-conversation-footer">
                                                        <span className="octopus-conversation-message">
                                                            {/*messageSender && `${messageSender}: `*/}
															{lastMessageText}
                                                        </span>
                                                        <span className="octopus-conversation-time">
                                                            {formatDate(conversation.date_sent || conversation.timestamp || conversation.updated_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {/* Load More Button (infinite scroll style) */}
                                    {conversationsPagination.has_next && (
                                        <div className="octopus-load-more-container">
                                            <button 
                                                onClick={handleLoadMoreConversations}
                                                disabled={isLoading}
                                                className="octopus-load-more-btn"
                                            >
                                                {isLoading ? 'Loading...' : 'Load More Conversations'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div
                        className={`octopus-chat-widget-bar ${!isMinimized ? 'is-hidden' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => { setIsMinimized(false); fetchUnreadCount(); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setIsMinimized(false); fetchUnreadCount(); } }}
                        aria-label="Open Messaging"
                    >
                        <div className="octopus-chat-widget-bar-avatar online-user">
                            {userAvatar ? (
                                <img src={userAvatar} alt="" />
                            ) : (
                                <span className="octopus-chat-widget-bar-initial">{userInitial}</span>
                            )}
                        </div>
                        <span className="octopus-chat-widget-bar-label">Messaging</span>
                        {unreadCount > 0 && (
                            <span className="octopus-chat-widget-bar-unread" aria-label={`${unreadCount} unread`}>
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                        <span 
                            className="octopus-icon bb-icon-f header-new-message bb-icon-edit"
                            onClick={() => {
                                setIsNewMessageMode(true);
                                setActiveConversation(null);
                            }}
                            style={{ cursor: 'pointer' }}
                            title="New message"
                        ></span>
                        <button
                            type="button"
                            className="octopus-chat-widget-bar-btn octopus-chat-widget-bar-chevron"
                            onClick={(e) => { e.stopPropagation(); setIsMinimized(false); fetchUnreadCount(); }}
                            aria-label="Expand"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                {/* Upside icon (chevron up) */}
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                            </svg>
                        </button>
                        
                    </div>
                )}    
            </div>
        </div>
    );
};

export default Messages;

